/**
 * iter290 — Membership fee + Low Fund Alert settings dialog.
 * Fee: auto-charged to every member each period (monthly/quarterly/yearly)
 * by the 8 AM IST cron; "Charge now" runs the current period immediately.
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { CreditCard, AlertTriangle, Zap } from 'lucide-react';

export default function MembershipFeeDialog({ open, onOpenChange, group, api, onSaved }) {
  const [feeEnabled, setFeeEnabled] = useState(false);
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [threshold, setThreshold] = useState('');
  const [saving, setSaving] = useState(false);
  const [charging, setCharging] = useState(false);

  useEffect(() => {
    if (!open || !group) return;
    const mf = group.membership_fee || {};
    const lfa = group.low_fund_alert || {};
    setFeeEnabled(!!mf.enabled);
    setAmount(mf.amount ? String(mf.amount) : '');
    setFrequency(mf.frequency || 'monthly');
    setAlertEnabled(!!lfa.enabled);
    setThreshold(lfa.threshold ? String(lfa.threshold) : '');
  }, [open, group]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/groups/${group.id}/membership-settings`, {
        fee_enabled: feeEnabled,
        fee_amount: parseFloat(amount) || 0,
        fee_frequency: frequency,
        low_fund_alert_enabled: alertEnabled,
        low_fund_threshold: parseFloat(threshold) || 0,
      });
      toast.success('Membership settings saved');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const chargeNow = async () => {
    const n = group?.member_ids?.length || 0;
    if (!window.confirm(`Charge ₹${parseFloat(amount) || 0} membership fee to all ${n} members now? Everyone gets a WhatsApp notification.`)) return;
    setCharging(true);
    try {
      const res = await api.post(`/groups/${group.id}/membership-fee/charge-now`);
      toast.success(res.data?.message || 'Membership fees charged');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Charge failed');
    } finally {
      setCharging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="membership-fee-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Membership &amp; Fund Alerts
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5 mt-4">
          <div className="p-3 border-2 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-semibold">Membership fee</Label>
                <p className="text-xs text-muted-foreground">Auto-charge every member each period to keep the fund topped up.</p>
              </div>
              <Switch checked={feeEnabled} onCheckedChange={setFeeEnabled} data-testid="membership-fee-toggle" />
            </div>
            {feeEnabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase tracking-wide">Amount (₹)</Label>
                    <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="membership-fee-amount" className="h-11 border-2 rounded-xl font-bold" required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase tracking-wide">Frequency</Label>
                    <div className="flex gap-1">
                      {['monthly', 'quarterly', 'yearly'].map((f) => (
                        <button key={f} type="button" onClick={() => setFrequency(f)} data-testid={`membership-freq-${f}`}
                          className={`flex-1 h-11 rounded-xl border-2 text-xs font-bold capitalize transition-colors ${frequency === f ? 'border-primary bg-primary/10 text-primary' : 'border-border'}`}>
                          {f.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Charged automatically at 8 AM IST when a new {frequency === 'monthly' ? 'month' : frequency === 'quarterly' ? 'quarter' : 'year'} starts — every member gets a WhatsApp. Enabling does NOT charge the current period; use the button below for that.
                </p>
                {group?.membership_fee?.enabled && (
                  <Button type="button" variant="outline" onClick={chargeNow} disabled={charging || !(parseFloat(amount) > 0)} data-testid="membership-charge-now-btn" className="w-full rounded-xl">
                    <Zap className="w-4 h-4 mr-1" /> {charging ? 'Charging…' : 'Charge current period now'}
                  </Button>
                )}
              </>
            )}
          </div>

          <div className="p-3 border-2 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Low fund alert</Label>
                <p className="text-xs text-muted-foreground">Warn admins + treasurer when the fund dips below a threshold.</p>
              </div>
              <Switch checked={alertEnabled} onCheckedChange={setAlertEnabled} data-testid="low-fund-alert-toggle" />
            </div>
            {alertEnabled && (
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-wide">Alert when fund below (₹)</Label>
                <Input type="number" min="1" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="low-fund-threshold-input" className="h-11 border-2 rounded-xl font-bold" required />
              </div>
            )}
          </div>

          <Button type="submit" disabled={saving} data-testid="membership-save-btn" className="w-full h-12 rounded-xl font-bold">
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
